import * as React from 'react';
import { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  IconButton,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CreateButtonWithModal from '../components/common/CreateButtonWithModal';
import BlackWhiteSwitch from '../components/common/BlackWhiteSwitch';
import DeleteButton from '../components/common/DeleteButton';
import EditModal from '../components/common/EditModal';

interface Column {
  id: keyof Data | 'action';
  label: string;
  minWidth?: number;
  align?: 'right' | 'center' | 'left';
}

interface Data {
  code: string;
  paymentType: string;
  status: string;
  createdBy: string;
}

const columns: readonly Column[] = [
  { id: 'paymentType', label: 'Payment Type', minWidth: 170 },
  { id: 'status', label: 'Status', minWidth: 100 },
  { id: 'createdBy', label: 'Created By', minWidth: 170 },
  { id: 'action', label: 'Action', minWidth: 100, align: 'center' },
];

function createData(code: string, paymentType: string, status: string, createdBy: string): Data {
  return { code, paymentType, status, createdBy };
}

const initialRows: Data[] = [
  createData('001', 'Credit Card', 'Active', 'Admin'),
  createData('002', 'PayPal', 'Inactive', 'John'),
  createData('003', 'Bank Transfer', 'Active', 'Emma'),
  createData('004', 'Cash', 'Active', 'Satoshi'),
];

const PaymentMethods = () => {
  const [rows, setRows] = useState<Data[]>(initialRows);
  const [filteredRows, setFilteredRows] = useState<Data[]>(initialRows);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('');

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSearch = () => {
    if (!filterBy || !searchTerm) {
      setFilteredRows(rows);
      return;
    }

    const lowerSearch = searchTerm.toLowerCase();
    const filtered = rows.filter((row) =>
      row[filterBy as keyof Data]?.toString().toLowerCase().includes(lowerSearch)
    );
    setFilteredRows(filtered);
  };

  const handleEditSave = (updatedData: Data) => {
    if (selectedRowIndex === null) return;
    const updatedRows = [...rows];
    updatedRows[selectedRowIndex] = updatedData;
    setRows(updatedRows);
    setFilteredRows(updatedRows);
    setEditOpen(false);
  };

  const handleDeleteRow = (code: string) => {
    const updatedRows = rows.filter((row) => row.code !== code);
    setRows(updatedRows);
    setFilteredRows(updatedRows);
    console.log(`Deleted row with code: ${code}`);
  };

  const renderStatusButton = (status: string) => (
    <Button
      variant="contained"
      size="small"
      sx={{
        backgroundColor: status === 'Active' ? '#1cad2d' : '#e31a24',
        color: 'white',
        fontWeight: 'bold',
        textTransform: 'none',
        '&:hover': {
          backgroundColor: status === 'Active' ? '#2e7d32' : '#c62828',
        },
      }}
    >
      {status}
    </Button>
  );

  const renderActions = (row: Data, index: number) => (
    <>
      <IconButton
        color="primary"
        size="small"
        onClick={() => {
          setSelectedRowIndex(index);
          setEditOpen(true);
        }}
      >
        <EditIcon />
      </IconButton>
      <DeleteButton
        onDelete={() => handleDeleteRow(row.code)}
        confirmMessage="Do you really want to delete this item?"
      />
    </>
  );

  React.useEffect(() => {
    setFilteredRows(rows);
  }, [rows]);

  return (
    <>
      <Box className="headingBtnWrap">
      <Typography variant="h1">Payment Methods</Typography>
        <CreateButtonWithModal modalTitle="Payment Methods">
          <Box className="modalFormWrap">
            <Box component="form" className="modalForm">
              <div className="form-control inputFieldWrap">
                <label>Payment Type</label>
                <input type="text" className="inputField" />
              </div>
            </Box>
            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" className="formBtn">
                Save
              </Button>
            </Box>
          </Box>
          <BlackWhiteSwitch />
        </CreateButtonWithModal>
      </Box>

      {/* <Box
        display="flex"
        alignItems="center"
        gap={2}
        p={2}
        sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}
        className="searchFilterDropdownWrap"
      >
        <TextField
          fullWidth
          variant="outlined"
          className="searchFilterInput"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') handleSearch();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton onClick={handleSearch} edge="start">
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <FormControl sx={{ minWidth: 120 }}>
          <Select
            labelId="filter-select-label"
            value={filterBy}
            onChange={(e) => setFilterBy(e.target.value)}
            displayEmpty
            className="searchFilterDropdown"
          >
            <MenuItem value=""><em>Select Filter</em></MenuItem>
            <MenuItem value="paymentType">Payment Type</MenuItem>
            <MenuItem value="status">Status</MenuItem>
            <MenuItem value="createdBy">Created By</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" className="searchBtn" onClick={handleSearch}>
          Search
        </Button>
      </Box> */}

      <Paper sx={{ width: '100%', overflowX: 'auto' }}>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader aria-label="payment methods table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                <TableRow hover key={row.code}>
                  {columns.map((column) => {
                    if (column.id === 'action') {
                      return (
                        <TableCell key={column.id} align="center">
                          {renderActions(row, index)}
                        </TableCell>
                      );
                    }
                    if (column.id === 'status') {
                      return (
                        <TableCell key={column.id}>
                          {renderStatusButton(row.status)}
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell key={column.id}>{row[column.id as keyof Data]}</TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
      </Paper>

      {/* Edit Modal */}
      {selectedRowIndex !== null && (
        <EditModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          initialData={rows[selectedRowIndex]}
          onSave={handleEditSave}
          excludeFields={['action', 'status']}
          readOnlyFields={['createdBy']}
          title="Edit Payment Methods"
        />
      )}
    </>
  );
};

export default PaymentMethods;
